/**
 * F39 (Wave 2C) — admin placement-inventory CMS page.
 *
 * Route: `/placement-inventory`
 * Permission: `placement_inventory:read` (super_admin + support + finance +
 *             content_moderator) — view.
 *             `placement_inventory:write` (super_admin) — edit a type.
 *
 * Renders the 7-type catalogue (base price / scope / scarcity cap / lead /
 * advance / tier-restriction / active) with an admin-editable "Edit" CTA per
 * row that opens `EditInventoryDialog` (If-Match + mandatory reason). The CTA is
 * hidden for actors without `placement_inventory:write`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Chip,
  CurrencyDisplay,
  Skeleton,
  Table,
  type TableColumn,
  useToast,
} from '@bravemove/ui-web';
import type { PlacementInventoryConfig } from '@bravemove/types';
import { adminPlacementsApi } from '../../api/placements';
import { translateApiError } from '../../auth/errors';
import { useAuth } from '../../auth/useAuth';
import { PageHeader } from '../../components/PageHeader';
import { EditInventoryDialog } from '../../components/placements/EditInventoryDialog';

export function PlacementInventoryPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const auth = useAuth();

  const canWrite = auth.adminUser?.permissions?.includes('placement_inventory:write') ?? false;

  const [rows, setRows] = useState<readonly PlacementInventoryConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<PlacementInventoryConfig | null>(null);

  const fetchCatalogue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminPlacementsApi.listInventory();
      setRows([...res.data].sort((a, b) => a.sortOrder - b.sortOrder));
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchCatalogue();
  }, [fetchCatalogue]);

  const columns = useMemo<readonly TableColumn<PlacementInventoryConfig>[]>(
    () => [
      {
        key: 'type',
        header: t('placement:admin.inventory.col_type', { defaultValue: 'Type' }),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span
              className="text-[13px] font-semibold text-ink"
              data-testid={`inventory-name-${row.placementType}`}
            >
              {t(`placement:type.${row.placementType}`, { defaultValue: row.displayName })}
            </span>
            <span className="font-mono text-[11px] text-ink-3">
              {t(`placement:scope_kind.${row.scopeKind}`, { defaultValue: row.scopeKind })}
            </span>
          </div>
        ),
      },
      {
        key: 'price',
        header: t('placement:admin.inventory.base_price', {
          defaultValue: 'Base price (per week)',
        }),
        align: 'end',
        cell: (row) => (
          <span data-testid={`inventory-price-${row.placementType}`}>
            <CurrencyDisplay
              amount={row.basePriceMinor}
              currency={row.priceCurrency}
              locale={locale}
            />
          </span>
        ),
      },
      {
        key: 'scarcity',
        header: t('placement:admin.inventory.scarcity_cap', { defaultValue: 'Scarcity cap' }),
        align: 'end',
        cell: (row) => (
          <span
            className="text-[12px] text-ink-2"
            data-testid={`inventory-cap-${row.placementType}`}
          >
            {row.scarcityCap === null
              ? t('placement:admin.inventory.uncapped', { defaultValue: 'Uncapped' })
              : row.scarcityCap}
          </span>
        ),
      },
      {
        key: 'lead',
        header: t('placement:admin.inventory.lead_advance', { defaultValue: 'Lead / advance' }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">
            {t('placement:admin.inventory.lead_advance_value', {
              lead: row.minLeadHours,
              advance: row.maxAdvanceDays,
              defaultValue: '{lead}h / {advance}d',
            })}
          </span>
        ),
      },
      {
        key: 'tier',
        header: t('placement:admin.inventory.tier_restriction', {
          defaultValue: 'Tier restriction',
        }),
        cell: (row) =>
          row.tierRestriction === 'pro_plus' ? (
            <Badge tone="accent" size="sm" data-testid={`inventory-tier-${row.placementType}`}>
              {t('placement:tier_restriction.pro_plus', { defaultValue: 'Pro plan and above' })}
            </Badge>
          ) : (
            <span className="text-ink-3" data-testid={`inventory-tier-${row.placementType}`}>
              {t('placement:admin.inventory.no_restriction', { defaultValue: 'All tiers' })}
            </span>
          ),
      },
      {
        key: 'active',
        header: t('placement:admin.inventory.active', { defaultValue: 'Active' }),
        cell: (row) =>
          row.active ? (
            <Badge tone="success" size="sm" data-testid={`inventory-active-${row.placementType}`}>
              {t('placement:admin.inventory.active_yes', { defaultValue: 'Active' })}
            </Badge>
          ) : (
            <Badge tone="neutral" size="sm" data-testid={`inventory-active-${row.placementType}`}>
              {t('placement:admin.inventory.active_no', { defaultValue: 'Disabled' })}
            </Badge>
          ),
      },
      {
        key: 'actions',
        header: t('placement:admin.inventory.col_actions', { defaultValue: 'Actions' }),
        align: 'end',
        cell: (row) => (
          <div className="flex justify-end">
            {canWrite ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditTarget(row)}
                data-testid={`inventory-edit-${row.placementType}`}
              >
                {t('placement:admin.inventory.edit', { defaultValue: 'Edit type' })}
              </Button>
            ) : (
              <Chip data-testid={`inventory-readonly-${row.placementType}`}>
                {t('placement:admin.inventory.readonly', { defaultValue: 'Read-only' })}
              </Chip>
            )}
          </div>
        ),
      },
    ],
    [t, locale, canWrite],
  );

  const onSaved = () => {
    toast.push({
      tone: 'success',
      title: t('placement:admin.inventory.toast_saved', {
        defaultValue: 'Placement type updated.',
      }),
    });
    void fetchCatalogue();
  };

  return (
    <div className="space-y-6" data-testid="placement-inventory-page">
      <PageHeader
        title={t('placement:admin.inventory.title', { defaultValue: 'Placement inventory' })}
        subtitle={t('placement:admin.inventory.subtitle', {
          defaultValue:
            'The placement catalogue. Base price, scarcity cap, lead/advance, and tier restriction are admin-editable.',
        })}
      />

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="placement-inventory-loading">
          {Array.from({ length: 7 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <Table
          data={rows as PlacementInventoryConfig[]}
          columns={columns as TableColumn<PlacementInventoryConfig>[]}
          rowKey={(row) => row.placementType}
        />
      )}

      {editTarget ? (
        <EditInventoryDialog
          open={editTarget !== null}
          config={editTarget}
          onConfirm={onSaved}
          onClose={() => setEditTarget(null)}
        />
      ) : null}
    </div>
  );
}

export default PlacementInventoryPage;
